import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ScrollReveal from '../../components/ui/ScrollReveal';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

const includes = [
  'Full nutritional and lifestyle assessment tailored to your goals',
  'Personalised calorie and macro strategy with practical targets',
  'Flexible meal framework with no restrictive fad-diet rules',
  'Ongoing review and adjustment as your body composition changes',
  'Habit coaching to improve consistency and food confidence',
  'Direct support and accountability throughout your programme',
];

const steps = [
  {
    number: '01',
    title: 'Nutrition Audit',
    body: 'We assess current habits, appetite patterns, schedule, and barriers that affect progress.',
  },
  {
    number: '02',
    title: 'Personal Strategy',
    body: 'Laura builds your tailored nutrition framework with targets you can actually sustain.',
  },
  {
    number: '03',
    title: 'Refine & Progress',
    body: 'You receive ongoing feedback and adjustments so results keep moving in the right direction.',
  },
];

const forYou = [
  'You want a trusted nutrition coach London clients recommend',
  'You are tired of restrictive plans and short-term fixes',
  'You want fat loss or performance gains without food anxiety',
  'You need expert accountability to stay consistent',
  'You are looking for a practical weight loss coach London clients can trust',
];

const expectations = [
  'Simple, clear nutritional structure with realistic flexibility',
  'Data-led adjustments rather than guesswork',
  'Clear habits that support long-term body composition change',
  'Education so you understand what works for your body',
  'A sustainable approach you can maintain beyond coaching',
];

export default function NutritionalCoaching() {
  useSEO({
    title: 'Nutrition Coach London | Laura Martin Fitness',
    description:
      'Expert nutrition coaching in London. Personalised macro targets, flexible meal strategies & sustainable habits — no fad diets.',
  });

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <Header />
      <main>
        <section className="relative h-[50vh] min-h-[400px] lg:h-[70vh] lg:min-h-[560px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1800&q=80"
              alt="Nutrition coach London healthy meal planning session"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1c1917]/80 via-[#1c1917]/45 to-transparent" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #faf8f5 0%, rgba(250,248,245,0.35) 18%, transparent 42%)' }} />
          </div>
          <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-10 pt-28 lg:pt-36">
            <div className="absolute bottom-0 pb-10 lg:pb-24 max-w-3xl">
              <span className="inline-block px-4 py-1.5 bg-[#c9a96e]/20 border border-[#c9a96e]/40 text-[#c9a96e] text-[9px] font-sans font-semibold tracking-[0.3em] uppercase rounded-full mb-5">
                Nutrition Coaching · London
              </span>
              <h1 className="text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(2.6rem, 5.5vw, 4.5rem)', fontWeight: 600, lineHeight: 1.0 }}>
                Nutritional
                <br />
                <em className="text-[#c9a96e] not-italic">Coaching</em>
              </h1>
              <p className="text-white/70 font-sans text-base max-w-xl">
                Smart, sustainable nutrition support that helps you transform without restriction, overwhelm, or fad diets.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#faf8f5] py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-16 items-start">
            <ScrollReveal>
              <div>
                <h2 className="text-stone-900 text-4xl mb-6 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Build Results Through Better Nutrition
                </h2>
                <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                  As a dedicated nutrition coach London clients trust, Laura creates personalised strategies that align with your goals, routine, and food preferences.
                </p>
                <p className="text-stone-600 font-sans text-base leading-relaxed mb-5">
                  Whether your target is fat loss, better performance, or improved relationship with food, your coaching plan is designed for sustainable progress and clear structure.
                </p>
                <p className="text-stone-600 font-sans text-base leading-relaxed">
                  If you need a practical weight loss coach London clients can work with long term, this service gives you the support, education, and accountability to stay on track.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={120}>
              <div className="bg-white rounded-2xl p-8 border border-stone-200 shadow-sm">
                <h3 className="text-stone-900 text-2xl mb-6 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {includes.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm leading-relaxed">
                      <span className="w-5 h-5 rounded-full bg-[#c9a96e]/12 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-[#c9a96e] text-[10px]" />
                      </span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section className="bg-stone-50 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-12">
              <h2 className="text-stone-900 text-4xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                How It Works
              </h2>
            </ScrollReveal>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {steps.map((step, index) => (
                <ScrollReveal key={step.title} delay={index * 80}>
                  <div className="h-full bg-white rounded-2xl border border-stone-200 p-8">
                    <span className="text-6xl leading-none text-[#c9a96e]/20 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.number}
                    </span>
                    <h3 className="text-stone-900 text-xl mt-4 mb-3 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      {step.title}
                    </h3>
                    <p className="text-stone-600 font-sans text-sm leading-relaxed">{step.body}</p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-stone-50 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10">
            <ScrollReveal className="mb-8">
              <h2 className="text-stone-900 text-4xl font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Is This Right For You?
              </h2>
            </ScrollReveal>
            <div className="bg-white border border-stone-200 rounded-2xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              <div>
                <h3 className="text-stone-900 text-2xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  This Is For You If...
                </h3>
                <ul className="space-y-3">
                  {forYou.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                      <i className="ri-checkbox-circle-line text-[#c9a96e] mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-stone-900 text-2xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  What To Expect...
                </h3>
                <ul className="space-y-3">
                  {expectations.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-stone-600 font-sans text-sm">
                      <i className="ri-checkbox-circle-line text-[#c9a96e] mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-stone-100 py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center">
            <ScrollReveal>
              <h2 className="text-stone-900 text-5xl mb-5 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Ready To Simplify Nutrition And See Results?
              </h2>
              <p className="text-stone-500 font-sans text-base max-w-2xl mx-auto mb-10">
                Book a free consultation and get a personalised nutrition roadmap designed for your body and your life.
              </p>
              <Link
                to="/contact"
                className="inline-block px-9 py-4 bg-[#c9a96e] text-white text-[10px] font-sans font-bold tracking-[0.28em] uppercase rounded-full hover:bg-[#a8863f] transition-all duration-300"
              >
                Book Free Consultation
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
